import React, { useEffect, useRef } from "react";
import { WheelItem, WheelList, WheelMask } from "./WheelColumn.styles";

const ITEM_HEIGHT = 44;
const VISIBLE_ITEMS = 5; // impar: el central es el seleccionado
const SCROLL_IDLE_MS = 80;
const PAGE_JUMP = VISIBLE_ITEMS; // salto al usar RePág/AvPág
// Delta de rueda acumulado necesario para avanzar un elemento (un "tick"
// de rueda de ratón típico reporta deltaY = 100).
const WHEEL_DELTA_PER_STEP = 100;

// Contador para generar ids únicos de las opciones (aria-activedescendant).
let wheelInstanceCount = 0;

interface WheelColumnProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  // Etiqueta accesible de la columna (p. ej. "Desde - horas").
  ariaLabel: string;
}

// Ruleta de una sola columna con patrón ARIA listbox. El valor centrado es el
// seleccionado; se puede cambiar por scroll (snap), clic o teclado (flechas,
// Inicio/Fin, RePág/AvPág). El foco vive en la lista y la selección sigue al foco.
const WheelColumn: React.FC<WheelColumnProps> = ({
  options,
  value,
  onChange,
  ariaLabel,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const idleTimer = useRef<number>();
  const wheelDelta = useRef(0);
  const baseId = useRef(`wheel-${(wheelInstanceCount += 1)}`);
  const optionId = (index: number) => `${baseId.current}-opt-${index}`;
  const currentIndex = Math.max(0, options.indexOf(value));

  // Centra la ruleta en el valor actual cuando cambia desde fuera.
  useEffect(() => {
    const index = options.indexOf(value);
    if (index < 0 || !listRef.current) return;
    const target = index * ITEM_HEIGHT;
    if (Math.abs(listRef.current.scrollTop - target) > 1) {
      listRef.current.scrollTop = target;
    }
  }, [value, options]);

  // Frena la rueda del ratón: en lugar del scroll nativo (demasiado rápido),
  // cada tick acumulado avanza un solo elemento. Listener nativo porque React
  // registra onWheel como pasivo y no permitiría preventDefault.
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      wheelDelta.current += event.deltaY;
      const steps = Math.trunc(wheelDelta.current / WHEEL_DELTA_PER_STEP);
      if (steps === 0) return;
      wheelDelta.current -= steps * WHEEL_DELTA_PER_STEP;
      const index = Math.max(
        0,
        Math.min(
          options.length - 1,
          Math.round(list.scrollTop / ITEM_HEIGHT) + steps
        )
      );
      list.scrollTo({ top: index * ITEM_HEIGHT, behavior: "smooth" });
    };
    list.addEventListener("wheel", handleWheel, { passive: false });
    return () => list.removeEventListener("wheel", handleWheel);
  }, [options.length]);

  const handleScroll = () => {
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      if (!listRef.current) return;
      const index = Math.max(
        0,
        Math.min(
          options.length - 1,
          Math.round(listRef.current.scrollTop / ITEM_HEIGHT)
        )
      );
      const next = options[index];
      if (next !== value) onChange(next);
    }, SCROLL_IDLE_MS);
  };

  // Selecciona por índice: ajusta el scroll (animado) y notifica el cambio.
  const selectIndex = (index: number) => {
    const clamped = Math.max(0, Math.min(options.length - 1, index));
    if (listRef.current) {
      listRef.current.scrollTo({
        top: clamped * ITEM_HEIGHT,
        behavior: "smooth",
      });
    }
    const next = options[clamped];
    if (next !== value) onChange(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        selectIndex(currentIndex + 1);
        break;
      case "ArrowUp":
        event.preventDefault();
        selectIndex(currentIndex - 1);
        break;
      case "PageDown":
        event.preventDefault();
        selectIndex(currentIndex + PAGE_JUMP);
        break;
      case "PageUp":
        event.preventDefault();
        selectIndex(currentIndex - PAGE_JUMP);
        break;
      case "Home":
        event.preventDefault();
        selectIndex(0);
        break;
      case "End":
        event.preventDefault();
        selectIndex(options.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <WheelMask $itemHeight={ITEM_HEIGHT} $visible={VISIBLE_ITEMS}>
      <WheelList
        ref={listRef}
        role="listbox"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-activedescendant={optionId(currentIndex)}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        $itemHeight={ITEM_HEIGHT}
        $visible={VISIBLE_ITEMS}
      >
        {options.map((opt, index) => (
          <WheelItem
            key={opt}
            id={optionId(index)}
            role="option"
            aria-selected={opt === value}
            $itemHeight={ITEM_HEIGHT}
            $selected={opt === value}
            onClick={() => selectIndex(index)}
          >
            {opt}
          </WheelItem>
        ))}
      </WheelList>
    </WheelMask>
  );
};

export default WheelColumn;
